import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus, Building2, UserPlus, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";

  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [orgName, setOrgName] = useState("");
  const [docEdits, setDocEdits] = useState({});
  const [savingDoc, setSavingDoc] = useState(null);
  const [nameEdits, setNameEdits] = useState({});
  const [savingName, setSavingName] = useState(null);
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "", role: "user", organization_ids: [] });
  const [editingUser, setEditingUser] = useState(null); // {id, email, organization_ids: []}
  const [resetUser, setResetUser] = useState(null); // {id, email}
  const [newPassword, setNewPassword] = useState("");

  const load = async () => {
    try {
      const [o, u] = await Promise.all([api.get("/organizations"), api.get("/users")]);
      setOrgs(o.data);
      setDocEdits(Object.fromEntries(o.data.map((org) => [org.id, org.documentation_url || ""])));
      setNameEdits(Object.fromEntries(o.data.map((org) => [org.id, org.name || ""])));
      setUsers(u.data);
    } catch (e) { if (!e?.__silent) toast.error("Failed to load admin data"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggleNewUserOrg = (oid) => {
    setNewUser((n) => ({
      ...n,
      organization_ids: n.organization_ids.includes(oid)
        ? n.organization_ids.filter((x) => x !== oid)
        : [...n.organization_ids, oid],
    }));
  };

  const toggleEditOrg = (oid) => {
    setEditingUser((e) => ({
      ...e,
      organization_ids: e.organization_ids.includes(oid)
        ? e.organization_ids.filter((x) => x !== oid)
        : [...e.organization_ids, oid],
    }));
  };

  const saveDoc = async (orgId) => {
    setSavingDoc(orgId);
    try {
      await api.put(`/organizations/${orgId}`, { documentation_url: (docEdits[orgId] || "").trim() });
      toast.success("Documentation link saved");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save link"); }
    finally { setSavingDoc(null); }
  };

  const saveName = async (orgId) => {
    const newName = (nameEdits[orgId] || "").trim();
    if (!newName) { toast.error("Name cannot be empty"); return; }
    setSavingName(orgId);
    try {
      await api.put(`/organizations/${orgId}`, { name: newName });
      toast.success("Organization renamed");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to rename"); }
    finally { setSavingName(null); }
  };

  const onAddOrg = async (e) => {
    e.preventDefault();
    if (!orgName.trim()) return;
    try {
      await api.post("/organizations", { name: orgName.trim() });
      setOrgName("");
      toast.success("Organization created");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const onAddUser = async (e) => {
    e.preventDefault();
    if (isSuper && newUser.organization_ids.length === 0) {
      toast.error("Pick at least one organization");
      return;
    }
    try {
      await api.post("/auth/register", {
        email: newUser.email.trim(),
        password: newUser.password,
        name: newUser.name,
        role: newUser.role,
        organization_ids: isSuper ? newUser.organization_ids : undefined,
      });
      setNewUser({ email: "", password: "", name: "", role: "user", organization_ids: newUser.organization_ids });
      toast.success("User created");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create user"); }
  };

  const saveEditUser = async () => {
    if (!editingUser) return;
    if (editingUser.organization_ids.length === 0) {
      toast.error("Pick at least one organization");
      return;
    }
    try {
      await api.put(`/users/${editingUser.id}/organizations`, { organization_ids: editingUser.organization_ids });
      toast.success("Access updated");
      setEditingUser(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const onResetPassword = async () => {
    if (!resetUser) return;
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await api.post(`/users/${resetUser.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${resetUser.email}`);
      setResetUser(null);
      setNewPassword("");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const onDeleteUser = async (id, email) => {
    if (!window.confirm(`Delete user ${email}?`)) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("User deleted");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const onChangeRole = async (id, email, newRole) => {
    if (!window.confirm(`Change ${email} to ${newRole}?`)) return;
    try {
      await api.put(`/users/${id}/role`, { role: newRole });
      toast.success(`${email} is now ${newRole}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update role"); }
  };

  const onResetUserMfa = async (id, email) => {
    if (!window.confirm(`Reset MFA for ${email}? They will be able to sign in with only their password until they re-enable it.`)) return;
    try {
      await api.post(`/users/${id}/reset-mfa`);
      toast.success(`MFA reset for ${email}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-page">
      <PageHeader
        eyebrow={isSuper ? "[06] Super Admin" : "[06] Org Admin"}
        title="Administration"
        subtitle={isSuper ? "Manage organizations and users across all clients." : `Manage users in ${user?.organization_name}.`}
      />
      <div className="p-4 sm:p-6 lg:p-8 space-y-10">

        {isSuper && (
          <section data-testid="orgs-section">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-4 h-4 text-[#1A1A1A]" strokeWidth={1.75} />
              <h2 className="font-mono text-xs tracking-[0.2em] uppercase text-[#1A1A1A]">Organizations</h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <form onSubmit={onAddOrg} className="lg:col-span-1 border border-[#E5E0D5] bg-white p-6 space-y-4 self-start">
                <div className="uppercase-label">New Organization</div>
                <Input data-testid="org-name-input" placeholder="e.g. Acme Ltd" value={orgName} onChange={(e) => setOrgName(e.target.value)} className={inputCls} />
                <Button type="submit" data-testid="add-org-btn" className="w-full rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase">
                  <Plus className="w-4 h-4 mr-2" /> Create Organization
                </Button>
              </form>
              <div className="lg:col-span-2 border border-[#E5E0D5] bg-white">
                <div className="px-6 py-3 border-b border-[#E5E0D5]">
                  <div className="uppercase-label">Defined ({orgs.length})</div>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E5E0D5]">
                      <Th>Name</Th>
                      <Th>Documentation Link</Th>
                      <Th align="right">Users</Th>
                      <Th align="right">Assets</Th>
                      <Th align="right">Created</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {orgs.map((o) => (
                      <tr key={o.id} data-testid={`org-row-${o.id}`} className="border-b border-[#E5E0D5]">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Input
                              data-testid={`org-name-input-${o.id}`}
                              value={nameEdits[o.id] ?? ""}
                              onChange={(e) => setNameEdits({ ...nameEdits, [o.id]: e.target.value })}
                              className="h-8 rounded-none bg-white border-[#D6CFC1] font-mono text-sm w-44"
                            />
                            <button
                              data-testid={`org-name-save-${o.id}`}
                              onClick={() => saveName(o.id)}
                              disabled={savingName === o.id || !(nameEdits[o.id] || "").trim() || (nameEdits[o.id] ?? "") === (o.name || "")}
                              className="px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-[#5A5A5A] border border-[#D6CFC1] hover:text-[#0F0F0F] hover:border-[#1A1A1A] disabled:opacity-40"
                              title="Rename organization"
                            >{savingName === o.id ? "…" : "Rename"}</button>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Input
                              data-testid={`org-doc-input-${o.id}`}
                              value={docEdits[o.id] ?? ""}
                              onChange={(e) => setDocEdits({ ...docEdits, [o.id]: e.target.value })}
                              placeholder="https://…confluence…"
                              className="h-8 rounded-none bg-white border-[#D6CFC1] font-mono text-xs w-52"
                            />
                            <button
                              data-testid={`org-doc-save-${o.id}`}
                              onClick={() => saveDoc(o.id)}
                              disabled={savingDoc === o.id || (docEdits[o.id] ?? "") === (o.documentation_url || "")}
                              className="px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-[#5A5A5A] border border-[#D6CFC1] hover:text-[#0F0F0F] hover:border-[#1A1A1A] disabled:opacity-40"
                              title="Save documentation link"
                            >{savingDoc === o.id ? "…" : "Save"}</button>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-xs text-[#3A3A3A]">{o.user_count}</td>
                        <td className="px-4 py-3 text-right font-mono text-xs text-[#3A3A3A]">{o.asset_count}</td>
                        <td className="px-4 py-3 text-right font-mono text-xs text-[#7A7A7A]">{o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        <section data-testid="users-section">
          <div className="flex items-center gap-2 mb-4">
            <UserPlus className="w-4 h-4 text-[#1A1A1A]" strokeWidth={1.75} />
            <h2 className="font-mono text-xs tracking-[0.2em] uppercase text-[#1A1A1A]">Users</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <form onSubmit={onAddUser} className="lg:col-span-1 border border-[#E5E0D5] bg-white p-6 space-y-4 self-start">
              <div className="uppercase-label">Invite User</div>
              <div>
                <Label className="uppercase-label">Email</Label>
                <Input data-testid="user-email-input" type="email" required value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className={`mt-2 ${inputCls}`} />
              </div>
              <div>
                <Label className="uppercase-label">Password</Label>
                <Input data-testid="user-password-input" type="text" required minLength={6} value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className={`mt-2 ${inputCls}`} />
              </div>
              <div>
                <Label className="uppercase-label">Name</Label>
                <Input data-testid="user-name-input" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className={`mt-2 ${inputCls}`} />
              </div>
              <div>
                <Label className="uppercase-label">Role</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                  <SelectTrigger data-testid="user-role-select" className={`mt-2 ${inputCls}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none bg-white border-[#E5E0D5]">
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {isSuper && (
                <div>
                  <Label className="uppercase-label">
                    Organizations <span className="text-[#5A5A5A] normal-case tracking-normal">({newUser.organization_ids.length} selected)</span>
                  </Label>
                  <div data-testid="user-orgs-checklist" className="mt-2 border border-[#D6CFC1] bg-white max-h-44 overflow-auto">
                    {orgs.length === 0 && <div className="px-3 py-2 font-mono text-[10px] text-[#7A7A7A]">No organizations defined yet.</div>}
                    {orgs.map((o) => {
                      const selected = newUser.organization_ids.includes(o.id);
                      return (
                        <button
                          key={o.id}
                          type="button"
                          data-testid={`new-user-org-${o.id}`}
                          onClick={() => toggleNewUserOrg(o.id)}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-left font-mono text-xs transition-colors ${
                            selected ? "bg-[#F37B2A] text-white" : "text-[#1A1A1A] hover:bg-[#F0EDE3]"
                          }`}
                        >
                          <span className={`w-3.5 h-3.5 border ${selected ? "border-white bg-white" : "border-[#A8A39A]"} flex items-center justify-center`}>
                            {selected && <span className="block w-2 h-2 bg-white" />}
                          </span>
                          {o.name}
                        </button>
                      );
                    })}
                  </div>
                  <div className="font-mono text-[10px] text-[#7A7A7A] mt-1">Click to tick. Tick all orgs this user can switch between.</div>
                </div>
              )}
              <Button type="submit" data-testid="add-user-btn" className="w-full rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase h-11">
                <Plus className="w-4 h-4 mr-2" /> Create User
              </Button>
            </form>

            <div className="lg:col-span-2 border border-[#E5E0D5] bg-white overflow-x-auto">
              <div className="px-6 py-3 border-b border-[#E5E0D5]">
                <div className="uppercase-label">Active ({users.length})</div>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E5E0D5]">
                    <Th>Email</Th>
                    <Th>Name</Th>
                    <Th>Role</Th>
                    {isSuper && <Th>Organization</Th>}
                    <Th align="right"></Th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-[#E5E0D5]">
                      <td className="px-4 py-3 font-mono text-xs text-[#0F0F0F]">
                        <span className="inline-flex items-center gap-2">
                          {u.email}
                          {u.mfa_enabled && (
                            <span
                              data-testid={`mfa-badge-${u.id}`}
                              title="MFA enabled"
                              className="inline-flex items-center px-1.5 py-0.5 border border-[#2D7A4F]/40 bg-[#E8F1EB] text-[#2D7A4F] text-[9px] uppercase tracking-wider"
                            >MFA</span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-[#3A3A3A]">{u.name || "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {u.role === "super_admin" || u.id === user.id ? (
                          <RoleBadge role={u.role} />
                        ) : (
                          <Select
                            value={u.role}
                            onValueChange={(v) => onChangeRole(u.id, u.email, v)}
                          >
                            <SelectTrigger
                              data-testid={`role-select-${u.id}`}
                              className="h-7 w-28 rounded-none border-[#D6CFC1] bg-white font-mono text-[10px] uppercase tracking-wider"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="rounded-none bg-white border-[#D6CFC1]">
                              <SelectItem value="user" data-testid={`role-option-user-${u.id}`}>User</SelectItem>
                              <SelectItem value="admin" data-testid={`role-option-admin-${u.id}`}>Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </td>
                      {isSuper && (
                        <td className="px-4 py-3 font-mono text-xs text-[#3A3A3A]">
                          {(u.organization_names || []).map((o) => o.name).join(" · ") || "—"}
                        </td>
                      )}
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {u.role !== "super_admin" && (
                            <button
                              onClick={() => { setResetUser({ id: u.id, email: u.email }); setNewPassword(""); }}
                              data-testid={`reset-pw-${u.id}`}
                              className="px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-[#5A5A5A] border border-[#D6CFC1] hover:text-[#0F0F0F] hover:border-[#1A1A1A]"
                              title="Reset password"
                            ><KeyRound className="w-3 h-3 inline mr-1" />Reset</button>
                          )}
                          {u.role !== "super_admin" && u.mfa_enabled && u.id !== user.id && (
                            <button
                              onClick={() => onResetUserMfa(u.id, u.email)}
                              data-testid={`reset-mfa-${u.id}`}
                              className="px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-[#5A5A5A] border border-[#D6CFC1] hover:text-[#0F0F0F] hover:border-[#1A1A1A]"
                              title="Reset MFA (after lost device)"
                            >Reset MFA</button>
                          )}
                          {isSuper && u.role !== "super_admin" && (
                            <button
                              onClick={() => setEditingUser({ id: u.id, email: u.email, organization_ids: u.organization_ids || [] })}
                              data-testid={`edit-orgs-${u.id}`}
                              className="px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-[#5A5A5A] border border-[#D6CFC1] hover:text-[#0F0F0F] hover:border-[#1A1A1A]"
                              title="Edit organization access"
                            >Orgs</button>
                          )}
                          {u.role !== "super_admin" && u.id !== user.id && (
                            <button onClick={() => onDeleteUser(u.id, u.email)} data-testid={`delete-user-${u.id}`} className="p-1.5 text-[#7A7A7A] hover:text-[#B91C1C]">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>

      {/* Edit-orgs dialog */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setEditingUser(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#D6CFC1] w-full max-w-md" data-testid="edit-orgs-dialog">
            <div className="px-5 py-3 border-b border-[#E5E0D5]">
              <div className="uppercase-label">Organization Access</div>
              <div className="font-mono text-sm text-[#0F0F0F] mt-1 truncate">{editingUser.email}</div>
            </div>
            <div className="p-5 space-y-2 max-h-72 overflow-auto">
              {orgs.map((o) => {
                const selected = editingUser.organization_ids.includes(o.id);
                return (
                  <button
                    key={o.id}
                    type="button"
                    data-testid={`edit-org-${o.id}`}
                    onClick={() => toggleEditOrg(o.id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left font-mono text-xs transition-colors ${
                      selected ? "bg-[#F37B2A] text-white" : "text-[#1A1A1A] hover:bg-[#F0EDE3]"
                    }`}
                  >
                    <span className={`w-3.5 h-3.5 border ${selected ? "border-white bg-white" : "border-[#A8A39A]"} flex items-center justify-center`}>
                      {selected && <span className="block w-2 h-2 bg-white" />}
                    </span>
                    {o.name}
                  </button>
                );
              })}
            </div>
            <div className="px-5 py-3 border-t border-[#E5E0D5] flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEditingUser(null)} className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#F0EDE3] font-mono text-xs tracking-wider uppercase">Cancel</Button>
              <Button type="button" onClick={saveEditUser} data-testid="save-edit-orgs" className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase">Save</Button>
            </div>
          </div>
        </div>
      )}
      {/* Reset password dialog */}
      {resetUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setResetUser(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#D6CFC1] w-full max-w-md" data-testid="reset-pw-dialog">
            <div className="px-5 py-3 border-b border-[#E5E0D5]">
              <div className="uppercase-label">Reset Password</div>
              <div className="font-mono text-sm text-[#0F0F0F] mt-1 truncate">{resetUser.email}</div>
            </div>
            <div className="p-5 space-y-3">
              <Label className="uppercase-label">New Password</Label>
              <Input
                data-testid="new-password-input"
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 6 characters"
                className={inputCls}
                autoFocus
              />
              <div className="font-mono text-[10px] text-[#7A7A7A]">Share this temporary password with the user securely. They can change it after logging in.</div>
            </div>
            <div className="px-5 py-3 border-t border-[#E5E0D5] flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setResetUser(null)} className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#F0EDE3] font-mono text-xs tracking-wider uppercase">Cancel</Button>
              <Button type="button" onClick={onResetPassword} data-testid="confirm-reset-pw" className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase">Reset Password</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RoleBadge({ role }) {
  const map = {
    super_admin: { label: "Super Admin", cls: "text-[#B45309] border-[rgba(180,83,9,0.3)] bg-[rgba(180,83,9,0.08)]" },
    admin: { label: "Admin", cls: "text-[#1A1A1A] border-[#D6CFC1] bg-[#F0EDE3]" },
    user: { label: "User", cls: "text-[#5A5A5A] border-[#E5E0D5] bg-white" },
  };
  const v = map[role] || map.user;
  return <span className={`inline-flex items-center font-mono text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 border ${v.cls}`}>{v.label}</span>;
}

function Th({ children, align = "left" }) {
  return <th className={`px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A] text-${align} font-medium`}>{children}</th>;
}

const inputCls = "rounded-none bg-white border-[#D6CFC1] font-mono text-sm focus-visible:ring-1 focus-visible:ring-[#F37B2A] focus-visible:ring-offset-0";
